<template>
  <DataSourceReauthenticateDialog v-if="dataSource" :data-source="dataSource" />

  <AzureSyncDialog
    v-if="dataSource"
    v-model:visibility="syncDialogVisibility"
    :datasource="dataSource"
    @groups-changed="fetchEmployees(true)" />

  <EmployeeDrawer
    v-if="selectedEmployee"
    v-model:visibility="employeeDrawerVisibility"
    :employee="selectedEmployee" />

  <main class="employee-grid">
    <div class="header-card">
      <DataSourceDetailStatsCard
        v-if="dataSource"
        ref="statsCard"
        :data-source="dataSource"
        @refetch-data-source="fetchDataSource"
        @sync-groups="syncDialogVisibility = true" />
    </div>

    <el-card class="employees-card" :body-style="{ height: '100%' }">
      <div class="grid h-full grid-rows-[auto_1fr]">
        <div class="table-header">
          <div class="flex items-center gap-4">
            <h4 class="light-text">
              {{ i18n.t('employees') + ` (${statsCard?.employeeCount || 0})` }}
            </h4>
            <div class="flex items-center gap-2">
                      <SmDropdown
                        :close-on-click-inside="false"
                        dropdown-class="w-[42rem] max-w-7xl">
                        <template #trigger>
                          <SmButton type="primary" size="small" outline>
                            <div class="flex items-center gap-2">
                              <Filter :size="16" />
                              <span>{{ i18n.t('filter') }}</span>
                              <span v-if="activeFilterCount > 0">
                                ({{ activeFilterCount }})
                              </span>
                            </div>
                          </SmButton>
                        </template>
            
                        <div style="padding: 1rem">
                          <EmployeeFilterDialog
                            v-model:filter="filters"
                            :employees="employees"
                            :close-on-click-inside="false" />
                        </div>
                      </SmDropdown>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <SmInput
              v-model:model-value="searchquery"
              size="small"
              outline
              style="width: 250px"
              :label="
                i18n.t(
                  'views.externalDataSourceDetails.employeesTable.searchPlaceholder'
                )
              " />
            <SmButton
              type="primary"
              size="small"
              @click="exportEmployees"
              :loading="exportLoading">
              <Download :size="16" class="mr-2" />
              {{ i18n.t('export') }}
            </SmButton>
          </div>
        </div>

        <EmployeeTable
          :employees="filteredEmployees"
          :searchquery="searchquery"
          :loading="false"
          height="auto"
          @row-click="handleEmployeeRowClick" />
      </div>
    </el-card>
  </main>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { ref, computed } from 'vue'
  import {
    DataSource,
    DataSourcesService,
    Employee,
    EmployeesService,
  } from '@/client'
  import { useRoute, useRouter } from 'vue-router'
  import DataSourceDetailStatsCard from '@/components/discovery/DataSourceDetailStatsCard.vue'
  import EmployeeDrawer from '@/components/discovery/EmployeeDrawer.vue'
  import EmployeeFilterDialog from '@/components/discovery/EmployeeFilterDialog.vue'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { Filter, Download } from 'lucide-vue-next'

  interface Option<T> {
    label: string
    value: T
  }

  const dataSource = ref<DataSource>()
  const employees = ref<Employee[]>([])
  const loading = ref(false)
  const syncDialogVisibility = ref(false)
  const statsCard = ref<ComponentExposed<typeof DataSourceDetailStatsCard>>()
  const searchquery = ref('')
  const exportLoading = ref(false)
  const selectedEmployee = ref<Employee | null>(null)
  const employeeDrawerVisibility = ref(false)

  // Filter state
  const filters = ref({
    status: {
      active: true,
      terminated: true,
    },
    department: [] as Option<string>[],
    position: [] as Option<string>[],
    dateRange: {
      hiredAfter: null as string | null,
      hiredBefore: null as string | null,
    },
  })

  // Helper function to get employee field from employee_external or application_specific
  const getEmployeeField = (employee: Employee, fieldName: string): any => {
    // Check in employee_external first
    if (employee.employee_external && employee.employee_external[fieldName]) {
      return employee.employee_external[fieldName]
    }
    // Check in application_specific
    if (employee.application_specific && employee.application_specific[fieldName]) {
      return employee.application_specific[fieldName]
    }
    return null
  }

  // Count active filters
  const activeFilterCount = computed(() => {
    let count = 0
    // Status filters
    if (!filters.value.status.active || !filters.value.status.terminated) count++
    // Department filter
    if (filters.value.department.length > 0) count++
    // Position filter
    if (filters.value.position.length > 0) count++
    // Date range filters
    if (filters.value.dateRange.hiredAfter || filters.value.dateRange.hiredBefore) count++
    return count
  })

  // Filter employees based on filters and search
  const filteredEmployees = computed(() => {
    return employees.value.filter((employee) => {
      // Search filter
      if (searchquery.value) {
        const query = searchquery.value.toLowerCase()
        const matchesSearch =
          employee.email.toLowerCase().includes(query) ||
          employee.name?.toLowerCase().includes(query)
        if (!matchesSearch) return false
      }

      // Status filter
      const isActive = !employee.end_date
      const isTerminated = !!employee.end_date
      if (!filters.value.status.active && isActive) return false
      if (!filters.value.status.terminated && isTerminated) return false

      // Department filter
      if (filters.value.department.length > 0) {
        const dept = getEmployeeField(employee, 'department')
        if (!dept || !filters.value.department.some(option => option.value === dept)) return false
      }

      // Position filter
      if (filters.value.position.length > 0) {
        if (!employee.position || !filters.value.position.some(option => option.value === employee.position)) return false
      }

      // Date range filter
      if (employee.start_date) {
        const startDate = new Date(employee.start_date)
        if (filters.value.dateRange.hiredAfter) {
          const hiredAfter = new Date(filters.value.dateRange.hiredAfter)
          if (startDate < hiredAfter) return false
        }
        if (filters.value.dateRange.hiredBefore) {
          const hiredBefore = new Date(filters.value.dateRange.hiredBefore)
          if (startDate > hiredBefore) return false
        }
      }

      return true
    })
  })

  // Export employees
  async function exportEmployees() {
    exportLoading.value = true
    try {
      // TODO: Implement export functionality
      console.log('Exporting employees:', filteredEmployees.value)
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
    } finally {
      exportLoading.value = false
    }
  }

  // Handle employee row click
  function handleEmployeeRowClick(employee: Employee) {
    selectedEmployee.value = employee
    employeeDrawerVisibility.value = true
  }

  const i18n = useI18n()
  const route = useRoute()
  const router = useRouter()

  function fetchDataSource() {
    if (!route.params.id) return router.push('/discovery')
    DataSourcesService.getDataSourceApiV1DataSourceDataSourcesDataSourceIdGet({
      dataSourceId: route.params.id as string,
    }).then((datasource) => {
      dataSource.value = datasource
    })
  }

  async function fetchEmployees(cached: boolean) {
    return EmployeesService.getDataSourceEmployeesApiV1DataSourceDataSourcesDataSourceIdEmployeesGet(
      {
        dataSourceId: route.params.id as string,
        cached: cached,
      }
    ).then((res) => {
      employees.value = res
    })
  }

  loading.value = true

  fetchDataSource()
  fetchEmployees(true)
  fetchEmployees(false).finally(() => {
    loading.value = false
  })
</script>

<style scoped lang="scss">
  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--el-border-color-light);
    margin-bottom: 1.5rem;
  }
  .light-text {
    color: var(--el-text-color-secondary);
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }

  .filter-panel {
    background-color: var(--el-fill-color-light);
    border-radius: var(--el-border-radius-base);
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid var(--el-border-color-light);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .filter-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--el-text-color-primary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .filter-options {
    display: flex;
    gap: 1rem;
  }

  .date-range-filters {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .date-filter {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .date-label {
    font-size: 0.75rem;
    color: var(--el-text-color-secondary);
    font-weight: 500;
  }

  .employee-grid {
    display: grid;
    height: 100%;
    width: 100%;
    gap: 24px;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'headerCard headerCard headerCard headerCard headerCard headerCard headerCard headerCard headerCard headerCard headerCard headerCard'
      'employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard employeesCard';

    .header-card {
      grid-area: headerCard;
      transition: all 0.3s ease;
    }

    .header-card:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .employees-card {
      grid-area: employeesCard;
      transition: all 0.3s ease;
    }

    .employees-card:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .optimizations-card {
      grid-area: optimizationsCard;
    }

    .lastActivity-card {
      grid-area: CardActivity;
    }
  }

  .datasource-sync-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 1rem;
    cursor: pointer;
  }

  /* Animation for filter panel */
  .filter-panel-enter-active,
  .filter-panel-leave-active {
    transition: all 0.3s ease;
    max-height: 500px;
    opacity: 1;
  }

  .filter-panel-enter-from,
  .filter-panel-leave-to {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 0;
    overflow: hidden;
  }

  /* Responsive adjustments */
  @media (max-width: 1200px) {
    .employee-grid {
      grid-template-columns: repeat(6, 1fr);
    }
  }

  @media (max-width: 768px) {
    .table-header {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
    }

    .filter-grid {
      grid-template-columns: 1fr;
    }

    .date-range-filters {
      flex-direction: column;
    }
  }

  /* Loading state animation */
  .loading-shimmer {
    background: linear-gradient(
      90deg,
      var(--el-fill-color-light) 25%,
      var(--el-fill-color-lighter) 50%,
      var(--el-fill-color-light) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
</style>
